import { PERSONA_IA_OPERACIONAL } from '@modules/aawz-intelligence-v2/constants/Personas'
import { modeloValidoOuPadrao } from '@modules/aawz-intelligence-v2/utils/modelos-suportados'
import {
  mensagensComConectores,
  partesComConectores,
} from '@modules/aawz-intelligence-v2/utils/conectores-no-thread'
import { hubEAgenteMutations } from './hub-e-agente'

export const SET_CONVERSATIONS = (state, payload) => {
  state.conversations = payload
}
// Acrescenta uma PÁGINA ao fim da lista, em vez de trocá-la — é o que o scroll
// infinito da lateral imersiva usa.
//
// ⚠️ Dedupe por `id` obrigatório: a paginação é por `offset` sobre a lista do
// servidor (por `created_at`), e uma conversa criada entre duas páginas
// desloca as demais — a repetida voltaria na seguinte. Sem isto ela
// renderizaria duas vezes, e a chave do `v-for` repetida faz o Vue reusar o
// nó errado. A ordem EXIBIDA (por `updated_at`) é do getter `conversations`.
export const APPEND_CONVERSATIONS = (state, payload) => {
  const novas = payload || []
  if (!novas.length) return
  const jaTem = new Set(state.conversations.map((conversa) => conversa.id))
  const ineditas = novas.filter((conversa) => !jaTem.has(conversa.id))
  if (!ineditas.length) return
  state.conversations = state.conversations.concat(ineditas)
}
// Carimba `updated_at` na conversa que acabou de receber mensagem, para ela
// subir em "Recentes" na hora — o servidor só grava o dele no fim do turno.
// Id ausente da lista (conversa nova, ainda não listada): nada a fazer.
export const TOUCH_CONVERSATION = (state, payload) => {
  const id = payload && payload.id
  if (!id || !state.conversations.some((c) => c.id === id)) return
  state.conversations = state.conversations.map(function (c) {
    return c.id === id ? { ...c, updated_at: payload.updated_at } : c
  })
}
export const SET_CONVERSATIONS_LOADING = (state, payload) => {
  state.conversationsLoading = payload
}
export const SET_CONVERSATIONS_LAST_UPDATE = (state, payload) => {
  state.conversationsLastUpdate = payload
}
export const SET_CONVERSATIONS_ERROR = (state, payload) => {
  state.conversationsError = Boolean(payload)
}

export const SET_FOLDERS = (state, payload) => {
  state.folders = Array.isArray(payload) ? payload : []
  state.foldersLastUpdate = Date.now()
}
export const SET_FOLDERS_LOADING = (state, payload) => {
  state.foldersLoading = payload
}
export const UPSERT_FOLDER = (state, payload) => {
  if (!payload || !payload.id) return
  const exists = state.folders.some((f) => f.id === payload.id)
  state.folders = exists
    ? state.folders.map((f) => (f.id === payload.id ? { ...f, ...payload } : f))
    : state.folders.concat([payload])
}
export const REMOVE_FOLDER = (state, id) => {
  state.folders = state.folders.filter((f) => f.id !== id)
}
export const SET_CONVERSATION_FOLDER = (state, payload) => {
  const id = payload && payload.id
  const idFolder = payload ? payload.id_folder : null
  if (!id) return
  state.conversations = state.conversations.map(function (c) {
    return c.id === id ? { ...c, id_folder: idFolder } : c
  })
}

export const SET_MESSAGES = (state, payload) => {
  state.messages = payload
}
// [VERIFY-3k/Q-11] Upsert por `id` — sem isso, drenar o backlog de um run
// TERMINAL (F5 numa conversa cujo turno já acabou, `apiChatModoAgente.js::
// _drenarBacklogTerminal`) reenviaria o MESMO `end` já persistido e
// carregado por `getMessages`, e o `.concat` duplicava a mensagem final na
// lista. `id` é o uuid ESTÁVEL da mensagem (mesmo em toda reconexão/replay
// do mesmo turno, [adv C-39]) — nunca dois turnos diferentes colidem nele.
// Campos que a mensagem PERSISTIDA tem e o replay do `end` não traz. O
// upsert por id substituía o objeto inteiro, e o replay apagava o que o
// histórico já tinha carregado. Medido na tela em 17/09, mutação a mutação:
//   SET_MESSAGES → assistente e26549e1 com `sources: 2` (vindo da API)
//   ADD_MESSAGE  → o MESMO id com `sources: 0` (montado do evento `end`)
// — e a seção Fontes sumia do painel ao reabrir a conversa.
// `fontes_web` (T051b/MUST-1, review PR #2328): o `end` já carimba o campo na
// mensagem ao vivo (`apiChat.js::_onEventEnd`, a partir de `_buscaWebDoTurno`
// — mesmo racional de `_planoDoTurno`), mas esta lista continua sendo a rede de
// segurança do upsert: um replay do MESMO `end` (backlog terminal, [T054])
// nunca apaga um `fontes_web` não-vazio já gravado (GET /messages ou turno
// anterior) por um vazio que o replay traga.
const CAMPOS_SO_DO_PERSISTIDO = ['sources', 'plano', 'thinking_steps', 'tool_calls', 'fontes_web']

export const ADD_MESSAGE = (state, payload) => {
  const id = payload && payload.id
  const idx = id ? state.messages.findIndex((m) => m.id === id) : -1
  if (idx !== -1) {
    const anterior = state.messages[idx] || {}
    // Merge, não substituição: o replay traz o texto e o desfecho, o
    // persistido traz o que o evento não carrega. Só preserva quando o novo
    // NÃO tem o campo — um `end` que de fato traga `sources` continua vencendo.
    const mesclado = { ...anterior, ...payload }
    for (const campo of CAMPOS_SO_DO_PERSISTIDO) {
      const novoValor = payload && payload[campo]
      const vazio = !novoValor || (Array.isArray(novoValor) && novoValor.length === 0)
      if (vazio && anterior[campo]) mesclado[campo] = anterior[campo]
    }
    state.messages = state.messages.map((m, i) => (i === idx ? mesclado : m))
    return
  }
  state.messages = state.messages.concat([payload])
}
export const SET_MESSAGES_LOADING = (state, payload) => {
  state.messagesLoading = payload
}
export const SET_MESSAGES_LAST_UPDATE = (state, payload) => {
  state.messagesLastUpdate = payload
}
export const SET_SELECTED_CONVERSATION = (state, payload) => {
  state.selectedConversation = payload
}

// Atualiza o feedback (thumbs_up/thumbs_down) de UMA mensagem já persistida —
// sem isso, o botão de feedback só reflete o clique visualmente até o
// componente re-renderizar do zero (nunca, na prática), porque `message.feedback`
// nunca era escrito de volta no store depois do POST em darFeedback (view-chat.vue).
//
// [spec-011 T003] `comentario`: mesma régua do backend em `resource_feedback.
// py` — só SOBRESCREVE `feedback_comentario` quando o payload trouxe algo;
// ausência preserva o comentário já gravado (em vez de apagar um comentário
// antigo quando o consultor só re-clica o thumb sem digitar nada novo).
export const UPDATE_MESSAGE_FEEDBACK = (state, payload) => {
  const id = payload && payload.id
  const feedback = payload && payload.feedback
  const comentario = payload && payload.comentario
  if (!id) return
  state.messages = state.messages.map(function (m) {
    if (m.id !== id) return m
    const atualizada = { ...m, feedback: feedback }
    if (comentario) atualizada.feedback_comentario = comentario
    return atualizada
  })
}

// Reescreve o estado dos conectores da etapa 3 do onboarding — no thread E nas
// partes VIVAS do turno, porque `parteDaChamada` consulta a viva primeiro e o
// slice só é esvaziado no turno seguinte (ver `utils/conectores-no-thread.js`).
//
// ⚠️ Só chega aqui o que foi APURADO: leitura que falhou omite a chave e o
// retrato do turno fica como está — desapurar por queda de rede seria pior que
// mostrar um dado velho.
export const ATUALIZAR_ESTADO_DOS_CONECTORES = (state, payload) => {
  const estados = payload || {}
  if (!Object.keys(estados).length) return
  state.messages = mensagensComConectores(state.messages, estados)
  state.toolParts = partesComConectores(state.toolParts, estados)
}

export const SET_CHAT_LOADING = (state, payload) => {
  state.chatLoading = payload
}
export const SET_RESPONSE_TOKENS = (state, payload) => {
  state.responseTokens = payload
}
export const ADD_RESPONSE_TOKEN = (state, payload) => {
  state.responseTokens = state.responseTokens.concat([payload])
}
export const ADD_RESPONSE_TOKENS = (state, payload) => {
  // Batch add — 1 alocação de array por janela de flush (ver apiChat buffer).
  const batch = Array.isArray(payload) ? payload : [payload]
  if (batch.length === 0) return
  state.responseTokens = state.responseTokens.concat(batch)
}
export const RESET_RESPONSE_TOKENS = (state) => {
  state.responseTokens = []
}

export const SET_THINKING_STEPS = (state, payload) => {
  state.thinkingSteps = payload
}
export const ADD_THINKING_STEP = (state, payload) => {
  state.thinkingSteps = state.thinkingSteps.concat([payload])
}
export const RESET_THINKING_STEPS = (state) => {
  state.thinkingSteps = []
}

export const SET_TOOL_CALLS = (state, payload) => {
  state.toolCalls = payload
}
// [VERIFY-3/Q-5] `payload` é `{tool, toolCallId}` (contrato events.json §1,
// `tool_call.data.toolCallId`) — antes era a string do nome da tool crua, e a
// mesma tool chamada 2x no turno gerava DUAS entradas idênticas na lista
// (`:key="toolName"` colidia em `chat-message.vue`, "Duplicate keys detected").
export const ADD_TOOL_CALL = (state, payload) => {
  state.toolCalls = state.toolCalls.concat([payload])
}
// `identificador` é `toolCallId` quando o evento o carrega; fallback pro nome
// da tool preserva o comportamento antigo quando ele vier ausente (contrato
// legado/omisso) — `(t.toolCallId || t.tool)` casa com o mesmo fallback usado
// ao ADICIONAR (`apiChatStream.js`).
//
// ★ merge PR #2242 — a entrada pode ser OBJETO ou STRING, e ler a chave sem
// checar o tipo é uma falha SILENCIOSA: quem alimenta `toolCalls` no namespace
// principal passou a ser `api/chat-event-handlers.js` (o roteador de
// `desenvolvimento`, que despacha `data.tool || ''` — string crua), enquanto o
// `apiChatStream.js` da spec-002 despacha `{tool, toolCallId}`. Com string,
// `t.toolCallId`/`t.tool` são `undefined`, a comparação nunca casa e a tool
// NUNCA sai da lista: `rotulo-do-turno.js::rotuloDaToolEmVoo` lê a ÚLTIMA da
// lista, então a linha de raciocínio ficaria presa em "Consultando cliente..."
// pelo resto do turno, sem erro nenhum no console.
export const REMOVE_TOOL_CALL = (state, identificador) => {
  state.toolCalls = state.toolCalls.filter(function (t) {
    const chave = (t && typeof t === 'object') ? (t.toolCallId || t.tool) : t
    return chave !== identificador
  })
}
export const RESET_TOOL_CALLS = (state) => {
  state.toolCalls = []
}

export const SET_PENDING_CONFIRMATION = (state, payload) => {
  state.pendingConfirmation = payload
}
export const RESOLVE_PENDING_CONFIRMATION = (state, resolved) => {
  if (!state.pendingConfirmation) return
  state.pendingConfirmation = {
    ...state.pendingConfirmation,
    resolved: resolved,
  }
}
export const RESET_PENDING_CONFIRMATION = (state) => {
  state.pendingConfirmation = null
}

export const SET_PROMPT_SUGGESTIONS = (state, payload) => {
  state.promptSuggestions = payload
}
export const SET_PROMPT_SUGGESTIONS_LOADING = (state, payload) => {
  state.promptSuggestionsLoading = payload
}

export const SET_PERSONA_MODE = (state, payload) => {
  state.personaMode = payload
}

export const SET_PERSONA = (state, payload) => {
  state.persona = payload || PERSONA_IA_OPERACIONAL
}

export const SET_AGENTE_ATIVO = (state, payload) => {
  state.agenteAtivoId = payload || ''
}

export const SET_CLIENTE_ESCOPO = (state, payload) => {
  state.clienteEscopo = payload || null
}

// spec-016 T035 — ver comentário do state.clienteDaConversa.
export const SET_CLIENTE_DA_CONVERSA = (state, payload) => {
  state.clienteDaConversa = payload || null
}

// spec-009 [T006] — ver comentário do state.clienteFixadoManualmente.
export const SET_CLIENTE_FIXADO_MANUALMENTE = (state, payload) => {
  state.clienteFixadoManualmente = payload || null
}

export const SET_SERVICOS_CITADOS_STREAMING = (state, payload) => {
  state.servicosCitadosStreaming = Array.isArray(payload) ? payload : []
}
export const SET_PERGUNTAS_AO_GESTOR_STREAMING = (state, payload) => {
  state.perguntasAoGestorStreaming = Array.isArray(payload) ? payload : []
}
export const RESET_PERGUNTAS_AO_GESTOR_STREAMING = (state) => {
  state.perguntasAoGestorStreaming = []
}
export const SET_PAINEL_STREAMING = (state, payload) => {
  state.painelStreaming = payload && payload.turnos ? payload : null
}
export const RESET_PAINEL_STREAMING = (state) => {
  state.painelStreaming = null
}
export const ADICIONAR_PAINEL_EVENTO = (state, payload) => {
  if (!payload) return
  state.painelEventosStreaming = state.painelEventosStreaming.concat([payload])
}
export const RESET_PAINEL_EVENTOS_STREAMING = (state) => {
  state.painelEventosStreaming = []
}
export const RESET_SERVICOS_CITADOS_STREAMING = (state) => {
  state.servicosCitadosStreaming = []
}
export const SET_SELECTED_MODEL = (state, model) => {
  state.selectedModel = model
}

export const SET_THINKING_ENABLED = (state, value) => {
  state.thinkingEnabled = value
}

// spec-016 T004 — mutação pura; quem persiste em localStorage é a action
// `setVozSelecionada`, que é a dona do IO (mesmo padrão de `SET_SELECTED_MODEL`).
export const SET_VOZ_SELECIONADA = (state, vozId) => {
  state.vozSelecionada = vozId || ''
}

/**
 * A lista viva chegou — e é ela que diz qual modelo vale.
 *
 * ⚠️ A reconciliação é INCONDICIONAL, não só "quando não há nada salvo"
 * (Q7-A08, 03/09/2026): era exatamente o id SALVO que estava obsoleto. Quem
 * tinha `claude-sonnet-4-6` no `localStorage` — modelo que saiu da lista —
 * mandava todo turno com ele e recebia 400.
 *
 * Mutação PURA: quem reescreve o `localStorage` é a action `setSupportedModels`,
 * que é dona do IO.
 */
export const SET_SUPPORTED_MODELS = (state, payload) => {
  state.supportedModels = (payload && payload.models) || []
  state.supportedModelsLoaded = true
  state.selectedModel = modeloValidoOuPadrao(state.selectedModel, payload)
}

export const UPDATE_CONVERSATION_TOPIC = (state, payload) => {
  const id = payload.id
  const topic = payload.topic
  state.conversations = state.conversations.map(function (c) {
    return c.id === id ? { ...c, topic: topic } : c
  })
  if (state.selectedConversation && state.selectedConversation.id === id) {
    state.selectedConversation = { ...state.selectedConversation, topic: topic }
  }
}

// ---- Curadoria de sugestões da IA (spec-003/005/006) -----------------------

export const SET_CURADORIA_GROUPS = (state, payload) => {
  state.curadoriaGroups = payload || []
}
export const SET_CURADORIA_SUMMARY = (state, payload) => {
  state.curadoriaSummary = payload || null
}
export const SET_CURADORIA_LOADING = (state, payload) => {
  state.curadoriaLoading = payload
}

// spec-006 — os três recortes da triagem (abas + progresso)
export const setCuradoriaPorStatus = function (state, payload) {
  state.curadoriaPorStatus = {
    aguardando: (payload && payload.aguardando) || [],
    aceita: (payload && payload.aceita) || [],
    declinada: (payload && payload.declinada) || [],
  }
}

export const SET_FASE_DO_TURNO = (state, payload) => {
  state.faseDoTurno = payload || null
}

// ★ [review #2364, item D] O último `node-status` do turno — `{no, estado}`
// ou `null` (zerado com a fase, no início e no fim do turno).
export const SET_NO_DO_TURNO = (state, payload) => {
  state.noDoTurno = payload || null
}

// ---- Modo Agente (spec-002-chat-agentico-estilo-claude-code, plan.md D13) --

// [VERIFY-3/Q-4] `retry_permitido` agora vem em `payload` quando a origem
// (evento `estado` ao vivo, OU `run-atual`/`GET /runs/{id}` na reidratação,
// contrato 1.4.12) já o carrega — antes esta mutation zerava incondicionalmente
// (`false`), então "Tentar de novo" sumia no primeiro reload de um run
// `failed` até chegar um `estado` novo. Fonte ausente (contrato antigo) ainda
// degrada bem: `Boolean(undefined) === false`.
// [VERIFY-3h, SHOULD-3 review-frontend-pr2242] `custo_creditos` só vem de
// `GET /runs/{id_run}` (`SET_RUN_CUSTO_CREDITOS`, chamado 1x na transição
// pra `done`) — NUNCA do snapshot de `run-atual` (contrato confere: o
// exemplo de `GET /conversas/{id}/run-atual` em `api.yaml` não tem o
// campo). Sem preservar, uma reidratação REPETIDA do MESMO run (troca de
// conversa e volta, ou remount do widget) reconstruía `runAtual` do zero e
// derrubava o valor já buscado — o chip voltava a "concluído" sem os
// créditos, e o watcher de `runAtual.estado` só refaz a busca numa
// TRANSIÇÃO pra `done` (se o snapshot já chega `done` igual ao que já
// estava, não há transição, e o valor nunca mais é buscado). Preserva só
// quando é o MESMO `id_run` — um run genuinamente novo não deve herdar
// crédito de outro.
export const SET_RUN_ATUAL = (state, payload) => {
  if (!payload) {
    state.runAtual = null
    return
  }
  const mesmoRun = Boolean(state.runAtual && state.runAtual.id_run === payload.id_run)
  const custoAnterior = mesmoRun ? state.runAtual.custo_creditos : undefined
  const custoCreditos = payload.custo_creditos === undefined ? custoAnterior : payload.custo_creditos
  // ★ [21/09/2026] A conversa DONA do run, para as abas se recortarem
  // (`utils/run-da-conversa.js`). Um snapshot do MESMO run sem o campo não
  // apaga o que já se sabia.
  const conversaAnterior = mesmoRun ? state.runAtual.id_conversa : null
  state.runAtual = {
    id_run: payload.id_run || null,
    id_conversa: payload.id_conversa || conversaAnterior || null,
    seq: payload.seq || 0,
    estado: payload.estado || null,
    motivo: payload.motivo || null,
    retry_permitido: Boolean(payload.retry_permitido),
    custo_creditos: custoCreditos,
  }
}
// Avança o cursor sem perder `estado`/`motivo` já conhecidos — chamada a
// cada grupo de frames completo (`apiChat.js::_avancarCursorSeNecessario`).
export const SET_RUN_SEQ = (state, seq) => {
  const base = state.runAtual || { id_run: null, estado: null, motivo: null }
  state.runAtual = { ...base, seq: seq }
}
export const SET_RUN_ESTADO = (state, payload) => {
  const base = state.runAtual || { id_run: null, seq: 0 }
  state.runAtual = {
    ...base,
    estado: (payload && payload.estado) || null,
    motivo: (payload && payload.motivo) || null,
    retry_permitido: Boolean(payload && payload.retry_permitido),
    // [VERIFY-3b/G-D17] Recibo[] da transição — chip só exibe em `cancelled`.
    efeitos_conhecidos: (payload && payload.efeitos_conhecidos) || [],
  }
}
export const RESET_RUN_ATUAL = (state) => {
  state.runAtual = null
}
// [VERIFY-2/G-D6b] `custo_creditos` vem de `GET /runs/{id_run}` (não do fio
// SSE) — chamado só quando o chip entra em `done` (view-chat.vue). Merge
// raso: preserva `id_run`/`seq`/`estado`/`motivo` já conhecidos.
export const SET_RUN_CUSTO_CREDITOS = (state, custo) => {
  const base = state.runAtual || { id_run: null, seq: 0, estado: null, motivo: null }
  state.runAtual = { ...base, custo_creditos: custo }
}

export const SET_RECONECTANDO = (state, payload) => {
  state.reconectando = Boolean(payload)
}

// Substitui pela versão MAIOR — o mesmo turno pode reemitir `plano` (TodoWrite
// repetido); nunca regride para uma versão mais antiga chegando fora de ordem.
// [VERIFY-3h, SHOULD-1 review-frontend-pr2242] `<=` (não `<`) — comparação
// ESTRITA, simétrica com `apiChat.js::_onPlano`: a
// MESMA versão reemitida com conteúdo divergente não deveria substituir o
// que já está na tela (só versão genuinamente maior substitui).
export const SET_PLANO = (state, payload) => {
  const versaoAtual = (state.plano && state.plano.versao) || 0
  const versaoNova = (payload && payload.versao) || 0
  if (state.plano && versaoNova <= versaoAtual) return
  state.plano = payload || null
}
export const RESET_PLANO = (state) => {
  state.plano = null
}

export const UPSERT_PROPOSTA = (state, payload) => {
  if (!payload || !payload.id_proposta) return
  const atual = state.propostasAbertas[payload.id_proposta] || {}
  state.propostasAbertas = {
    ...state.propostasAbertas,
    [payload.id_proposta]: { ...atual, ...payload },
  }
}
export const RESOLVE_PROPOSTA = (state, payload) => {
  const id = payload && payload.id_proposta
  if (!id || !state.propostasAbertas[id]) return
  const atual = state.propostasAbertas[id]
  state.propostasAbertas = {
    ...state.propostasAbertas,
    [id]: { ...atual, estado: 'decidida', resultado: payload.resultado, recibo: payload.recibo || null },
  }
}
export const INVALIDAR_PROPOSTA = (state, payload) => {
  const id = payload && payload.id_proposta
  if (!id || !state.propostasAbertas[id]) return
  const atual = state.propostasAbertas[id]
  state.propostasAbertas = {
    ...state.propostasAbertas,
    [id]: { ...atual, estado: 'invalidada', substituidaPor: payload.substituida_por || null },
  }
}
export const RESET_PROPOSTAS_ABERTAS = (state) => {
  state.propostasAbertas = {}
}

export const ADICIONAR_BUSCA_STREAMING = (state, payload) => {
  if (!payload) return
  // ★ [spec-002 T050/T051] Carimbo da DATA DE COLETA.
  //
  // O payload `d_busca` do worker não tem campo de data — o envelope tem
  // `timestamp_worker`, mas `api/chat-event-handlers.js` desembrulha o evento
  // antes de chegar aqui (e é assim que o contrato do front foi fixado). Sem um
  // carimbo, o cartão de fonte (`chat-message-fontes-web`) teria de escrever
  // "agora" ou ficar sem data, e a US-5 pede a data JUNTO da fonte.
  //
  // O que este campo significa, com precisão: **quando esta tela recebeu o
  // resultado**. Não é quando o provedor indexou a página. A diferença é de
  // segundos e está escrita no componente para ninguém ler como data do
  // conteúdo. Um `coletado_em` que já venha no payload (se o worker passar a
  // mandar um) VENCE o carimbo local — a origem melhor sempre ganha.
  const comCarimbo = payload.coletado_em
    ? payload
    : { ...payload, coletado_em: new Date().toISOString() }
  state.buscaStreaming = state.buscaStreaming.concat([comCarimbo])
}
export const RESET_BUSCA_STREAMING = (state) => {
  state.buscaStreaming = []
}
export const ADICIONAR_CODIGO_STREAMING = (state, payload) => {
  if (!payload) return
  state.codigoStreaming = state.codigoStreaming.concat([payload])
}
export const RESET_CODIGO_STREAMING = (state) => {
  state.codigoStreaming = []
}
export const ADICIONAR_ARQUIVO_STREAMING = (state, payload) => {
  if (!payload) return
  state.arquivoStreaming = state.arquivoStreaming.concat([payload])
}
export const RESET_ARQUIVO_STREAMING = (state) => {
  state.arquivoStreaming = []
}

export const SET_FINISH_DO_TURNO = (state, payload) => {
  state.finishDoTurno = payload || null
}

export const SET_PLANO_DO_TURNO = (state, payload) => {
  state.planoDoTurno = payload || null
}

// ── D6 (spec-010, [T028]) + RF-G2 — hub do turno e agentes da mensagem ─────
//
// As três mutations moram no slice COMPARTILHADO `hub-e-agente.js`: o
// `apiChat.js` despacha as actions correspondentes pelo namespace de quem o
// instanciou, e as três superfícies de chat (`aawz-intelligence-v2`,
// `feeFixoIa`, `aiFramework`) precisam das MESMAS. Reexportadas aqui para que
// quem já importava por nome continue achando — uma implementação, três
// composições. Mesmo movimento do `toolParts.js`.
export const SET_HUB_DA_CONVERSA = hubEAgenteMutations.SET_HUB_DA_CONVERSA
export const SET_AGENTE_DA_MENSAGEM = hubEAgenteMutations.SET_AGENTE_DA_MENSAGEM
export const SET_AGENTES_DO_TURNO_AO_VIVO =
  hubEAgenteMutations.SET_AGENTES_DO_TURNO_AO_VIVO

export const SET_INSIGHTS = (state, payload) => {
  state.insights = payload || []
  // Carimbo aqui, e não em quem chama: o cache da API lê este valor para
  // decidir se vai à rede, e um dispatch que esquecesse de setá-lo faria a
  // coluna recarregar a cada montagem.
  state.insightsLastUpdate = new Date()
}
export const SET_INSIGHTS_LOADING = (state, payload) => {
  state.insightsLoading = payload
}
export const SET_INSIGHTS_ERROR = (state, payload) => {
  state.insightsError = Boolean(payload)
}

export const SET_SUGESTOES_A_TRIAR = (state, payload) => {
  // `Number.isFinite`, não o global: `isFinite(null)` é `true` e viraria o
  // "não apurado" em "0 sugestões" — só número de verdade sobrevive.
  state.sugestoesATriar = Number.isFinite(payload) ? payload : null
}
export const SET_SUGESTOES_A_TRIAR_LOADING = (state, payload) => {
  state.sugestoesATriarLoading = Boolean(payload)
}

// Vínculo WhatsApp: `vinculo` = `value` do `GET/PATCH/DELETE /whatsapp/vinculo`
// (ou `{status: 'nenhum'}`). `null` volta ao "não apurado".
export const SET_WHATSAPP_VINCULO = (state, vinculo) => {
  state.whatsappVinculo = vinculo
    ? { ...vinculo, carregado: true }
    : { status: null, carregado: false }
}
